import { Popover, Spin, Typography } from 'antd';
import { ReactNode, useState } from 'react';
import { lookupCached } from './mentionCache';

/** 信息卡里的一行：[标签, 值] */
export type MentionInfoRow = [string, ReactNode];

interface Props {
  /** 标签展示内容（如 @张三丰 / 客户名） */
  label: ReactNode;
  color: string;
  background: string;
  /** 缓存键，同一 id 只回查一次 */
  cacheKey: string;
  /** 回查并映射成信息卡行；返回 null 表示范围外或不存在 */
  load: () => Promise<MentionInfoRow[] | null>;
  /** 范围外/不存在时的提示文案 */
  missingText: string;
}

/**
 * 消息里的提及标签：内联高亮，hover 弹出信息卡（懒加载 + 缓存）。
 * 员工与客户共用，差异由 label / load / 配色注入。
 */
const MentionTag = (props: Props) => {
  const { label, color, background, cacheKey, load, missingText } = props;
  /** undefined = 未加载；null = 范围外或不存在 */
  const [rows, setRows] = useState<MentionInfoRow[] | null | undefined>(undefined);
  const [failed, setFailed] = useState(false);

  const doLoad = () => {
    setFailed(false);
    lookupCached(cacheKey, load)
      .then(setRows)
      .catch(() => setFailed(true));
  };

  let content: ReactNode;
  if (failed) {
    content = <Typography.Text type="secondary">加载失败，请重试</Typography.Text>;
  } else if (rows === undefined) {
    content = <Spin size="small" />;
  } else if (rows === null) {
    content = <Typography.Text type="secondary">{missingText}</Typography.Text>;
  } else {
    content = (
      <div style={{ minWidth: 190 }}>
        {rows.map(([rowLabel, value]) => (
          <div key={rowLabel} style={{ display: 'flex', gap: 8, lineHeight: '22px' }}>
            <Typography.Text type="secondary" style={{ width: 56, flexShrink: 0 }}>
              {rowLabel}
            </Typography.Text>
            <span>{value}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <Popover
      content={content}
      onOpenChange={(open) => {
        if (open && (rows === undefined || failed)) {
          doLoad();
        }
      }}
    >
      <span style={{ color, background, borderRadius: 4, padding: '0 4px', cursor: 'pointer' }}>
        {label}
      </span>
    </Popover>
  );
};

export default MentionTag;
